import { useState, useEffect } from 'react';
import Layout from '../components/Layout';
import axios from 'axios';
import { API } from '../App';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Activity, Server, Cpu, HardDrive } from 'lucide-react';
import { LineChart, Line, AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from 'recharts';
import { toast } from 'sonner';
import RealTimeMetrics from '../components/RealTimeMetrics';
import CostBreakdown from '../components/CostBreakdown';
import EnhancedPodNodeDetails from '../components/EnhancedPodNodeDetails';
import RealTimeAlertsDashboard from '../components/RealTimeAlertsDashboard';
import CostAllocationPanel from '../components/CostAllocationPanel';
import EfficiencyPanel from '../components/EfficiencyPanel';
import SavingsPanel from '../components/SavingsPanel';
import BudgetsPanel from '../components/BudgetsPanel';
import { MonitoringProvider, useMonitoring } from '../context/MonitoringContext';
// FinOps v2 (ClickHouse) analytics — each panel self-fetches and degrades to
// hidden when ClickHouse is off (503), so the legacy charts below always render.
import CostExplorer from '../components/finops/CostExplorer';
import NodesPanel from '../components/finops/NodesPanel';
import TopWorkloadsTable from '../components/finops/TopWorkloadsTable';
import UnitCostTiles from '../components/finops/UnitCostTiles';
import AnomalyFeed from '../components/finops/AnomalyFeed';
import CostTrendCard from '../components/finops/CostTrendCard';
import FinopsOverviewStrip from '../components/finops/FinopsOverviewStrip';
import ResourceTimeseriesPanel from '../components/finops/ResourceTimeseriesPanel';


import { parseUtc } from '../lib/parseUtc';
function Monitoring() {
  const [clusters, setClusters] = useState([]);
  const [selectedCluster, setSelectedCluster] = useState('');
  const [loading, setLoading] = useState(true);
  const [timeRange, setTimeRange] = useState(24); // hours — controlled by Layout header button
  const selectedClusterObj = clusters.find((cluster) => cluster.id === selectedCluster);

  // Listen for time range changes dispatched by the Layout header button
  useEffect(() => {
    const handler = (e) => setTimeRange(e.detail.hours);
    window.addEventListener('monitoring-time-range-change', handler);
    return () => window.removeEventListener('monitoring-time-range-change', handler);
  }, []);

  useEffect(() => {
    fetchClusters();
  }, []);

  const fetchClusters = async () => {
    try {
      const response = await axios.get(`${API}/clusters`);
      setClusters(response.data);
      if (response.data.length > 0) {
        setSelectedCluster(response.data[0].id);
      }
    } catch (error) {
      toast.error('Failed to load clusters');
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <Layout>
        <div className="space-y-6">
          <div className="skeleton h-8 w-48 rounded"></div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {[1, 2, 3].map((i) => (
              <div key={i} className="skeleton h-32 rounded-xl"></div>
            ))}
          </div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="space-y-8 animate-fade-in">
        {/* Header */}
        <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
          <div>
            <h1 className="text-3xl font-semibold text-slate-900 dark:text-white tracking-tight">Real-Time Monitoring</h1>
            <p className="text-slate-500 dark:text-text-secondary mt-1.5 text-sm flex items-center">
              <Activity className="w-3.5 h-3.5 mr-1.5 text-slate-400" />
              Track resource usage and costs across your clusters
            </p>
          </div>

          <Select value={selectedCluster} onValueChange={setSelectedCluster}>
            <SelectTrigger className="w-[200px]" data-testid="cluster-select">
              <SelectValue placeholder="Select cluster" />
            </SelectTrigger>
            <SelectContent>
              {clusters.map((cluster) => (
                <SelectItem key={cluster.id} value={cluster.id}>
                  {cluster.name}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        {clusters.length === 0 ? (
          <Card className="dark:bg-surface-dark dark:border-surface-border">
            <CardContent className="py-16">
              <div className="text-center">
                <Server className="w-16 h-16 mx-auto text-slate-300 dark:text-slate-600 mb-4" />
                <h3 className="text-lg font-semibold text-slate-900 dark:text-white mb-2">
                  No Clusters Available
                </h3>
                <p className="text-slate-600 dark:text-text-secondary">
                  Add a cluster to start monitoring
                </p>
              </div>
            </CardContent>
          </Card>
        ) : selectedCluster ? (
          <MonitoringProvider
            clusterId={selectedCluster}
            connectionMode={selectedClusterObj?.connection_mode}
            timeRange={timeRange}
          >
            <MonitoringContent clusterId={selectedCluster} />
          </MonitoringProvider>
        ) : null}
      </div>
    </Layout>
  );
}

// Inner component that uses the monitoring context
function MonitoringContent({ clusterId }) {
  const {
    metrics,
    realTimeData,
    costPerHour,
    costPerMinute,
    costPerSecond,
    dailyCost,
    monthlyCost,
    podsCount,
    nodesCount,
    cpuUsage,
    memoryUsage,
    loading,
    timeRange,
    finops,
    shadow,
    projection,
    mtdSavings,
    mtdSavingsPct,
    budget,
    refreshBudget,
  } = useMonitoring();

  // Small presentational card used by the FinOps live/cost summaries.
  const fcard = (label, value, sub, accent) => (
    <Card className="border border-slate-200 dark:border-surface-border bg-white dark:bg-surface-dark shadow-sm">
      <CardContent className="p-5">
        <p className="text-xs font-medium uppercase tracking-wide text-slate-500 dark:text-text-secondary mb-2">{label}</p>
        <p className={`text-3xl font-semibold tabular-nums ${accent || 'text-slate-900 dark:text-white'}`}>{value}</p>
        {sub && <p className="text-xs text-slate-500 dark:text-text-secondary mt-1">{sub}</p>}
      </CardContent>
    </Card>
  );
  const fLive = finops?.live || {};
  const fAlloc = finops?.allocation || {};
  const hasLive = fLive && (fLive.cpu_usage_pct != null || fLive.pods != null);

  // Convert timestamp strings to epoch ms so Recharts can use numeric domain
  const chartData = (metrics || []).map(m => ({
    ...m,
    _ts: parseUtc(m.timestamp).getTime(),
  }));

  // XAxis domain: always show the full selected time window
  const now = Date.now();
  const hours = timeRange || 24;
  const xDomain = [now - hours * 60 * 60 * 1000, now];

  // Adaptive tick formatter
  const xTickFormatter = (value) => {
    const d = new Date(value);
    if (hours <= 6) return d.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
    if (hours <= 48) return d.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
    return d.toLocaleDateString([], { month: 'short', day: 'numeric' });
  };

  if (loading && metrics.length === 0) {
    return (
      <Card className="dark:bg-surface-dark dark:border-surface-border">
        <CardContent className="py-16">
          <div className="text-center">
            <Activity className="w-16 h-16 mx-auto text-slate-300 dark:text-slate-600 mb-4 animate-pulse" />
            <h3 className="text-lg font-semibold text-slate-900 dark:text-white mb-2">
              Loading Metrics...
            </h3>
          </div>
        </CardContent>
      </Card>
    );
  }

  if (metrics.length === 0 && !realTimeData) {
    return (
      <Card className="dark:bg-surface-dark dark:border-surface-border" id="tour-monitoring-empty-state">
        <CardContent className="py-16">
          <div className="text-center">
            <Activity className="w-16 h-16 mx-auto text-slate-300 dark:text-slate-600 mb-4" />
            <h3 className="text-lg font-semibold text-slate-900 dark:text-white mb-2">
              No Metrics Available
            </h3>
            <p className="text-slate-600 dark:text-text-secondary">
              Sync your cluster to start collecting metrics
            </p>
          </div>
        </CardContent>
      </Card>
    );
  }
  return (
    <Tabs defaultValue="overview" className="w-full">
      <TabsList
        className="grid w-full grid-cols-4 md:grid-cols-7 bg-slate-100 dark:bg-surface-dark rounded-lg border border-slate-200 dark:border-surface-border p-1"
        id="tour-monitoring-tabs"
      >
        <TabsTrigger value="overview" className="text-sm font-medium data-[state=active]:bg-white dark:data-[state=active]:bg-slate-800 data-[state=active]:text-slate-900 dark:data-[state=active]:text-white data-[state=active]:shadow-sm">Overview</TabsTrigger>
        <TabsTrigger value="allocation" className="text-sm font-medium data-[state=active]:bg-white dark:data-[state=active]:bg-slate-800 data-[state=active]:text-slate-900 dark:data-[state=active]:text-white data-[state=active]:shadow-sm">Cost Analytics</TabsTrigger>
        <TabsTrigger value="nodes" className="text-sm font-medium data-[state=active]:bg-white dark:data-[state=active]:bg-slate-800 data-[state=active]:text-slate-900 dark:data-[state=active]:text-white data-[state=active]:shadow-sm">Nodes</TabsTrigger>
        <TabsTrigger value="efficiency" className="text-sm font-medium data-[state=active]:bg-white dark:data-[state=active]:bg-slate-800 data-[state=active]:text-slate-900 dark:data-[state=active]:text-white data-[state=active]:shadow-sm">Efficiency</TabsTrigger>
        <TabsTrigger value="budgets" className="text-sm font-medium data-[state=active]:bg-white dark:data-[state=active]:bg-slate-800 data-[state=active]:text-slate-900 dark:data-[state=active]:text-white data-[state=active]:shadow-sm">Budgets</TabsTrigger>
        <TabsTrigger value="savings" className="text-sm font-medium data-[state=active]:bg-white dark:data-[state=active]:bg-slate-800 data-[state=active]:text-slate-900 dark:data-[state=active]:text-white data-[state=active]:shadow-sm">Savings</TabsTrigger>
        <TabsTrigger value="details" className="text-sm font-medium data-[state=active]:bg-white dark:data-[state=active]:bg-slate-800 data-[state=active]:text-slate-900 dark:data-[state=active]:text-white data-[state=active]:shadow-sm">Details &amp; Events</TabsTrigger>
      </TabsList>

      <TabsContent value="allocation" className="space-y-6">
        {/* NEW: FinOps v2 cost explorer — cluster→namespace→workload drill-down,
            stacked cost-over-time, Δ vs prev period, CSV. Hides when CH is off. */}
        <CostExplorer clusterId={clusterId} hours={timeRange || 24} />
        {/* Existing label-aware allocation (group-by label, CSV + PDF export) kept below */}
        <CostAllocationPanel clusterId={clusterId} embedded />
        {/* Per-pod cost breakdown (existing component) */}
        <CostBreakdown clusterId={clusterId} />
      </TabsContent>

      <TabsContent value="nodes" className="space-y-6">
        {/* NEW: per-node cost, spot vs on-demand + coverage, sortable node table */}
        <NodesPanel clusterId={clusterId} hours={timeRange || 24} />
      </TabsContent>

      <TabsContent value="efficiency" className="space-y-6">
        {/* Live CPU & memory — ClickHouse-backed, shown when fresh data exists */}
        {hasLive && (
          <div>
            <div className="flex items-center justify-between mb-3">
              <h3 className="text-base font-semibold text-slate-900 dark:text-white">Live Usage</h3>
              <span className="text-xs text-slate-400 dark:text-slate-500">from ClickHouse{fLive.timestamp ? ` · ${parseUtc(fLive.timestamp).toLocaleTimeString()}` : ''}</span>
            </div>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              {fcard('CPU Usage', `${fLive.cpu_usage_pct ?? '—'}%`, `${fLive.cpu_used_cores ?? 0} / ${fLive.cpu_capacity_cores ?? 0} cores`)}
              {fcard('Memory Usage', `${fLive.mem_usage_pct ?? '—'}%`, `${fLive.mem_used_gb ?? 0} / ${fLive.mem_capacity_gb ?? 0} GB`)}
              {fcard('Active Pods', `${fLive.pods ?? 0}`, `${fLive.nodes ?? 0} nodes`)}
              {fcard('Nodes', `${fLive.nodes ?? 0}`)}
            </div>
          </div>
        )}
        {/* NEW: unit economics ($/CPU-hr provisioned vs effective) + top wasters */}
        <UnitCostTiles clusterId={clusterId} hours={timeRange || 24} />
        <TopWorkloadsTable clusterId={clusterId} hours={timeRange || 24} />
        <EfficiencyPanel finops={finops} clusterId={clusterId} />
      </TabsContent>

      <TabsContent value="budgets" className="space-y-6">
        <BudgetsPanel clusterId={clusterId} projection={projection} budget={budget} onSaved={refreshBudget} />
      </TabsContent>

      <TabsContent value="savings" className="space-y-6">
        <SavingsPanel clusterId={clusterId} mtdSavings={mtdSavings} mtdSavingsPct={mtdSavingsPct} shadow={shadow} />
      </TabsContent>

      <TabsContent value="overview" className="space-y-6">
        {/* NEW: multi-cluster portfolio strip (only shows when >1 owned cluster) */}
        <FinopsOverviewStrip hours={timeRange || 24} activeClusterId={clusterId} />

        {/* Current Stats - Using consistent data from context */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <Card
            className="border border-slate-200 dark:border-surface-border bg-white dark:bg-surface-dark shadow-sm hover:shadow transition-shadow"
            data-testid="pods-count-card"
            id="tour-active-resources"
          >
            <CardContent className="p-5">
              <div className="flex items-center justify-between mb-3">
                <p className="text-xs font-medium uppercase tracking-wide text-slate-500 dark:text-text-secondary">Active Pods</p>
                <div className="p-2 bg-blue-50 dark:bg-blue-500/10 rounded-md">
                  <Server className="w-4 h-4 text-blue-600 dark:text-blue-400" />
                </div>
              </div>
              <p className="text-3xl font-semibold text-slate-900 dark:text-white tabular-nums">
                {podsCount}
              </p>
              <p className="text-xs text-slate-500 dark:text-text-secondary mt-1">{nodesCount} nodes</p>
            </CardContent>
          </Card>

          <Card
            className="border border-slate-200 dark:border-surface-border bg-white dark:bg-surface-dark shadow-sm hover:shadow transition-shadow"
            data-testid="cpu-usage-card"
            id="tour-cpu-usage-card"
          >
            <CardContent className="p-5">
              <div className="flex items-center justify-between mb-3">
                <p className="text-xs font-medium uppercase tracking-wide text-slate-500 dark:text-text-secondary">CPU Usage</p>
                <div className="p-2 bg-violet-50 dark:bg-violet-500/10 rounded-md">
                  <Cpu className="w-4 h-4 text-violet-600 dark:text-violet-400" />
                </div>
              </div>
              <p className="text-3xl font-semibold text-slate-900 dark:text-white tabular-nums">
                {cpuUsage.toFixed(1)}<span className="text-lg text-slate-400 dark:text-slate-500 font-normal ml-1">%</span>
              </p>
              <p className="text-xs text-slate-500 dark:text-text-secondary mt-1">of capacity</p>
            </CardContent>
          </Card>

          <Card
            className="border border-slate-200 dark:border-surface-border bg-white dark:bg-surface-dark shadow-sm hover:shadow transition-shadow"
            data-testid="memory-usage-card"
            id="tour-memory-usage-card"
          >
            <CardContent className="p-5">
              <div className="flex items-center justify-between mb-3">
                <p className="text-xs font-medium uppercase tracking-wide text-slate-500 dark:text-text-secondary">Memory Usage</p>
                <div className="p-2 bg-rose-50 dark:bg-rose-500/10 rounded-md">
                  <HardDrive className="w-4 h-4 text-rose-600 dark:text-rose-400" />
                </div>
              </div>
              <p className="text-3xl font-semibold text-slate-900 dark:text-white tabular-nums">
                {memoryUsage.toFixed(1)}<span className="text-lg text-slate-400 dark:text-slate-500 font-normal ml-1">%</span>
              </p>
              <p className="text-xs text-slate-500 dark:text-text-secondary mt-1">of capacity</p>
            </CardContent>
          </Card>

          <Card
            className="border border-slate-200 dark:border-surface-border bg-white dark:bg-surface-dark shadow-sm hover:shadow transition-shadow"
            data-testid="cost-card"
            id="tour-daily-cost-card"
          >
            <CardContent className="p-5">
              <div className="flex items-center justify-between mb-3">
                <p className="text-xs font-medium uppercase tracking-wide text-slate-500 dark:text-text-secondary">Daily Cost</p>
                <div className="p-2 bg-emerald-50 dark:bg-emerald-500/10 rounded-md">
                  <Activity className="w-4 h-4 text-emerald-600 dark:text-emerald-400" />
                </div>
              </div>
              <p className="text-3xl font-semibold text-slate-900 dark:text-white tabular-nums">
                <span className="text-xl text-slate-400 dark:text-slate-500 font-normal">$</span>{dailyCost.toFixed(2)}
              </p>
              <p className="text-xs text-slate-500 dark:text-text-secondary mt-1">${monthlyCost.toFixed(0)}/month projected</p>
            </CardContent>
          </Card>
        </div>

        {/* NEW: FinOps spend-by-capacity trend + top wasters + anomalies.
            Each self-fetches from /finops/v2 and hides when ClickHouse is off. */}
        <CostTrendCard clusterId={clusterId} hours={timeRange || 24}
                       groupBy="capacity_type" title="Spend by capacity type"
                       hint="on-demand vs spot · with projection" showForecast hideWhenUnavailable />
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          <TopWorkloadsTable clusterId={clusterId} hours={timeRange || 24} limit={8} />
          <AnomalyFeed clusterId={clusterId} />
        </div>

        {/* Industry-level resource time-series (ClickHouse, FULL selected window,
            adaptive bucketing ≤300 pts). Replaces the row-capped legacy /metrics
            charts that only ever showed the last ~200 raw snapshots (≈2h). */}
        <ResourceTimeseriesPanel clusterId={clusterId} hours={timeRange || 24} />

        {/* Legacy time-series — fallback ONLY when ClickHouse (FinOps) has no
            series (off, or no CH data for this cluster). /metrics returns ~200 raw
            snapshots (≈2h) regardless of range, so it's the last resort. */}
        {!(finops && finops.time_series && finops.time_series.length) && (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* CPU Usage Over Time */}
          <Card
            id="tour-cpu-usage-chart"
            className="border border-slate-200 dark:border-surface-border bg-white dark:bg-surface-dark shadow-sm"
          >
            <CardHeader className="border-b border-slate-100 dark:border-surface-border pb-3">
              <CardTitle className="text-sm font-semibold flex items-center text-slate-700 dark:text-white">
                <span className="w-2 h-2 bg-violet-500 rounded-full mr-2"></span>
                CPU Usage
              </CardTitle>
            </CardHeader>
            <CardContent className="pt-6">
              <ResponsiveContainer width="100%" height={250}>
                <LineChart data={chartData} margin={{ top: 10, right: 10, left: 0, bottom: 0 }}>
                  <defs>
                    <linearGradient id="cpuLineGradient" x1="0" y1="0" x2="1" y2="0">
                      <stop offset="0%" stopColor="#8b5cf6" />
                      <stop offset="100%" stopColor="#a78bfa" />
                    </linearGradient>
                  </defs>
                  <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" opacity={0.5} />
                  <XAxis
                    dataKey="_ts"
                    type="number"
                    domain={xDomain}
                    stroke="#94a3b8"
                    fontSize={11}
                    tickLine={false}
                    axisLine={false}
                    tickFormatter={xTickFormatter}
                  />
                  <YAxis
                    stroke="#94a3b8"
                    fontSize={11}
                    tickLine={false}
                    axisLine={false}
                    domain={[0, 100]}
                    tickFormatter={(value) => `${value}%`}
                  />
                  <Tooltip
                    contentStyle={{
                      backgroundColor: 'rgba(15, 23, 42, 0.96)',
                      border: 'none',
                      borderRadius: '16px',
                      boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.5)',
                      padding: '16px 20px',
                      backdropFilter: 'blur(12px)'
                    }}
                    labelStyle={{ color: '#f1f5f9', fontWeight: '700', marginBottom: '12px', fontSize: '13px' }}
                    itemStyle={{ color: '#e2e8f0', fontSize: '14px', fontWeight: '500', padding: '4px 0' }}
                    cursor={{ stroke: '#8b5cf6', strokeWidth: 2, strokeDasharray: '5 5', opacity: 0.5 }}
                    labelFormatter={(value) => new Date(value).toLocaleString()}
                    formatter={(value) => [`${Number(value).toFixed(1)}%`, 'CPU']}
                  />
                  <Line
                    type="monotone"
                    dataKey="cpu_usage"
                    stroke="url(#cpuLineGradient)"
                    strokeWidth={2.5}
                    dot={false}
                    activeDot={false}
                    animationDuration={1500}
                    animationEasing="ease-in-out"
                  />
                </LineChart>
              </ResponsiveContainer>
            </CardContent>
          </Card>

          {/* Memory Usage Over Time */}
          <Card
            id="tour-memory-usage-chart"
            className="border border-slate-200 dark:border-surface-border bg-white dark:bg-surface-dark shadow-sm"
          >
            <CardHeader className="border-b border-slate-100 dark:border-surface-border pb-3">
              <CardTitle className="text-sm font-semibold flex items-center text-slate-700 dark:text-white">
                <span className="w-2 h-2 bg-rose-500 rounded-full mr-2"></span>
                Memory Usage
              </CardTitle>
            </CardHeader>
            <CardContent className="pt-6">
              <ResponsiveContainer width="100%" height={250}>
                <LineChart data={chartData} margin={{ top: 10, right: 10, left: 0, bottom: 0 }}>
                  <defs>
                    <linearGradient id="memoryLineGradient" x1="0" y1="0" x2="1" y2="0">
                      <stop offset="0%" stopColor="#ec4899" />
                      <stop offset="100%" stopColor="#f472b6" />
                    </linearGradient>
                  </defs>
                  <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" opacity={0.5} />
                  <XAxis
                    dataKey="_ts"
                    type="number"
                    domain={xDomain}
                    stroke="#94a3b8"
                    fontSize={11}
                    tickLine={false}
                    axisLine={false}
                    tickFormatter={xTickFormatter}
                  />
                  <YAxis
                    stroke="#94a3b8"
                    fontSize={11}
                    tickLine={false}
                    axisLine={false}
                    domain={[0, 100]}
                    tickFormatter={(value) => `${value}%`}
                  />
                  <Tooltip
                    contentStyle={{
                      backgroundColor: 'rgba(15, 23, 42, 0.96)',
                      border: 'none',
                      borderRadius: '16px',
                      boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.5)',
                      padding: '16px 20px',
                      backdropFilter: 'blur(12px)'
                    }}
                    labelStyle={{ color: '#f1f5f9', fontWeight: '700', marginBottom: '12px', fontSize: '13px' }}
                    itemStyle={{ color: '#e2e8f0', fontSize: '14px', fontWeight: '500', padding: '4px 0' }}
                    cursor={{ stroke: '#ec4899', strokeWidth: 2, strokeDasharray: '5 5', opacity: 0.5 }}
                    labelFormatter={(value) => new Date(value).toLocaleString()}
                    formatter={(value) => [`${Number(value).toFixed(1)}%`, 'Memory']}
                  />
                  <Line
                    type="monotone"
                    dataKey="memory_usage"
                    stroke="url(#memoryLineGradient)"
                    strokeWidth={2.5}
                    dot={false}
                    activeDot={false}
                    animationDuration={1500}
                    animationBegin={100}
                    animationEasing="ease-in-out"
                  />
                </LineChart>
              </ResponsiveContainer>
            </CardContent>
          </Card>

          {/* Active Pods Over Time */}
          <Card
            id="tour-pods-chart"
            className="border border-slate-200 dark:border-surface-border bg-white dark:bg-surface-dark shadow-sm"
          >
            <CardHeader className="border-b border-slate-100 dark:border-surface-border pb-3">
              <CardTitle className="text-sm font-semibold flex items-center text-slate-700 dark:text-white">
                <span className="w-2 h-2 bg-blue-500 rounded-full mr-2"></span>
                Active Pods
              </CardTitle>
            </CardHeader>
            <CardContent className="pt-6">
              <ResponsiveContainer width="100%" height={250}>
                <AreaChart data={chartData} margin={{ top: 10, right: 10, left: 0, bottom: 0 }}>
                  <defs>
                    <linearGradient id="podsGradient" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="0%" stopColor="#3b82f6" stopOpacity={0.4} />
                      <stop offset="50%" stopColor="#60a5fa" stopOpacity={0.2} />
                      <stop offset="100%" stopColor="#93c5fd" stopOpacity={0.05} />
                    </linearGradient>
                  </defs>
                  <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" opacity={0.5} />
                  <XAxis
                    dataKey="_ts"
                    type="number"
                    domain={xDomain}
                    stroke="#94a3b8"
                    fontSize={11}
                    tickLine={false}
                    axisLine={false}
                    tickFormatter={xTickFormatter}
                  />
                  <YAxis
                    stroke="#94a3b8"
                    fontSize={11}
                    tickLine={false}
                    axisLine={false}
                  />
                  <Tooltip
                    contentStyle={{
                      backgroundColor: 'rgba(15, 23, 42, 0.96)',
                      border: 'none',
                      borderRadius: '16px',
                      boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.5)',
                      padding: '16px 20px',
                      backdropFilter: 'blur(12px)'
                    }}
                    labelStyle={{ color: '#f1f5f9', fontWeight: '700', marginBottom: '12px', fontSize: '13px' }}
                    itemStyle={{ color: '#e2e8f0', fontSize: '14px', fontWeight: '500', padding: '4px 0' }}
                    cursor={{ stroke: '#3b82f6', strokeWidth: 2, strokeDasharray: '5 5', opacity: 0.5 }}
                    labelFormatter={(value) => new Date(value).toLocaleString()}
                    formatter={(value) => [value, 'Pods']}
                  />
                  <Area
                    type="monotone"
                    dataKey="pods_count"
                    stroke="#3b82f6"
                    strokeWidth={2.5}
                    fill="url(#podsGradient)"
                    dot={false}
                    activeDot={false}
                    animationDuration={1500}
                    animationEasing="ease-in-out"
                  />
                </AreaChart>
              </ResponsiveContainer>
            </CardContent>
          </Card>

          {/* Cost Over Time */}
          <Card className="border border-slate-200 dark:border-surface-border bg-white dark:bg-surface-dark shadow-sm">
            <CardHeader className="border-b border-slate-100 dark:border-surface-border pb-3">
              <CardTitle className="text-sm font-semibold flex items-center text-slate-700 dark:text-white">
                <span className="w-2 h-2 bg-emerald-500 rounded-full mr-2"></span>
                Daily Cost
              </CardTitle>
            </CardHeader>
            <CardContent className="pt-6">
              <ResponsiveContainer width="100%" height={250}>
                <AreaChart data={chartData} margin={{ top: 10, right: 10, left: 0, bottom: 0 }}>
                  <defs>
                    <linearGradient id="costGradientMonitoring" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="0%" stopColor="#10b981" stopOpacity={0.4} />
                      <stop offset="50%" stopColor="#34d399" stopOpacity={0.2} />
                      <stop offset="100%" stopColor="#6ee7b7" stopOpacity={0.05} />
                    </linearGradient>
                  </defs>
                  <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" opacity={0.5} />
                  <XAxis
                    dataKey="_ts"
                    type="number"
                    domain={xDomain}
                    stroke="#94a3b8"
                    fontSize={11}
                    tickLine={false}
                    axisLine={false}
                    tickFormatter={xTickFormatter}
                  />
                  <YAxis
                    stroke="#94a3b8"
                    fontSize={11}
                    tickLine={false}
                    axisLine={false}
                    tickFormatter={(value) => `$${Number(value).toFixed(2)}`}
                  />
                  <Tooltip
                    contentStyle={{
                      backgroundColor: 'rgba(15, 23, 42, 0.96)',
                      border: 'none',
                      borderRadius: '16px',
                      boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.5)',
                      padding: '16px 20px',
                      backdropFilter: 'blur(12px)'
                    }}
                    labelStyle={{ color: '#f1f5f9', fontWeight: '700', marginBottom: '12px', fontSize: '13px' }}
                    itemStyle={{ color: '#e2e8f0', fontSize: '14px', fontWeight: '500', padding: '4px 0' }}
                    cursor={{ stroke: '#10b981', strokeWidth: 2, strokeDasharray: '5 5', opacity: 0.5 }}
                    labelFormatter={(value) => new Date(value).toLocaleString()}
                    formatter={(value) => [`$${Number(value).toFixed(2)}`, 'Cost']}
                  />
                  <Area
                    type="monotone"
                    dataKey="cost_estimate"
                    stroke="#10b981"
                    strokeWidth={2.5}
                    fill="url(#costGradientMonitoring)"
                    dot={false}
                    activeDot={false}
                    animationDuration={1500}
                    animationEasing="ease-in-out"
                  />
                </AreaChart>
              </ResponsiveContainer>
            </CardContent>
          </Card>
        </div>
        )}

        {/* Current Cost Information - Using consistent data from context */}
        <Card
          className="border border-slate-200 dark:border-surface-border bg-white dark:bg-surface-dark shadow-sm"
          data-testid="cost-info-card"
          id="tour-cost-info-card"
        >
          <CardHeader className="border-b border-slate-100 dark:border-surface-border pb-3">
            <CardTitle className="text-sm font-semibold dark:text-white">Cost Rate Breakdown</CardTitle>
          </CardHeader>
          <CardContent className="pt-5">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div className="border border-slate-200 dark:border-surface-border rounded-md p-4">
                <p className="text-xs font-medium uppercase tracking-wide text-slate-500 dark:text-text-secondary">Per Second</p>
                <p className="text-2xl font-semibold text-slate-900 dark:text-white mt-2 tabular-nums">
                  <span className="text-base text-slate-400 dark:text-slate-500 font-normal">$</span>{costPerSecond.toFixed(4)}
                </p>
              </div>
              <div className="border border-slate-200 dark:border-surface-border rounded-md p-4">
                <p className="text-xs font-medium uppercase tracking-wide text-slate-500 dark:text-text-secondary">Per Minute</p>
                <p className="text-2xl font-semibold text-slate-900 dark:text-white mt-2 tabular-nums">
                  <span className="text-base text-slate-400 dark:text-slate-500 font-normal">$</span>{costPerMinute.toFixed(3)}
                </p>
              </div>
              <div className="border border-slate-200 dark:border-surface-border rounded-md p-4">
                <p className="text-xs font-medium uppercase tracking-wide text-slate-500 dark:text-text-secondary">Per Hour</p>
                <p className="text-2xl font-semibold text-slate-900 dark:text-white mt-2 tabular-nums">
                  <span className="text-base text-slate-400 dark:text-slate-500 font-normal">$</span>{costPerHour.toFixed(2)}
                </p>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Live real-time detail (folded in from the former Real-time tab) */}
        <RealTimeMetrics clusterId={clusterId} />
      </TabsContent>

      <TabsContent value="details" className="space-y-6">
        <EnhancedPodNodeDetails clusterId={clusterId} />
        <RealTimeAlertsDashboard clusterId={clusterId} />
      </TabsContent>
    </Tabs>
  );
}

export default Monitoring;
